
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChevronUp, ChevronDown, Lightbulb, AlertTriangle } from 'lucide-react';

interface SWOTAnalysisProps {
  analysis?: string;
  isLoading?: boolean;
}

const SWOTAnalysis: React.FC<SWOTAnalysisProps> = ({ analysis, isLoading }) => {
  const [expanded, setExpanded] = React.useState(true);

  // Function to parse and structure the SWOT analysis
  const parseAnalysis = () => {
    if (!analysis) return { strengths: [], weaknesses: [], opportunities: [], threats: [] };

    // Very basic parsing - this could be improved based on actual output format
    const sections = {
      strengths: [],
      weaknesses: [],
      opportunities: [],
      threats: []
    };

    // Simple rule-based parsing of the text
    const lines = analysis.split('\n');
    let currentSection = '';

    lines.forEach(line => {
      const trimmedLine = line.trim();
      
      if (trimmedLine.toLowerCase().includes('strength')) {
        currentSection = 'strengths';
      } else if (trimmedLine.toLowerCase().includes('weakness')) {
        currentSection = 'weaknesses';
      } else if (trimmedLine.toLowerCase().includes('opportunit')) {
        currentSection = 'opportunities';
      } else if (trimmedLine.toLowerCase().includes('threat')) {
        currentSection = 'threats';
      } else if (trimmedLine.startsWith('-') || trimmedLine.startsWith('•') || /^\d+\./.test(trimmedLine)) {
        if (currentSection && sections[currentSection]) {
          sections[currentSection].push(trimmedLine.replace(/^[-•\d.]+\s*/, ''));
        }
      }
    });

    return sections;
  };

  const { strengths, weaknesses, opportunities, threats } = parseAnalysis();

  if (!analysis && !isLoading) return null;

  return (
    <Card className="w-full border-primary/20 overflow-hidden">
      <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 py-3 px-4">
        <div className="flex justify-between items-center">
          <CardTitle className="text-base font-semibold text-blue-900 flex items-center">
            <Lightbulb className="h-4 w-4 text-blue-500 mr-2" />
            Learning Analysis
          </CardTitle>
          <button 
            onClick={() => setExpanded(!expanded)}
            className="text-gray-500 hover:text-gray-700"
          >
            {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
        </div>
      </CardHeader>
      
      {expanded && (
        <CardContent className="p-4">
          {isLoading ? (
            <div className="flex justify-center p-6">
              <div className="animate-pulse flex space-x-4">
                <div className="flex-1 space-y-4 py-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-green-700 flex items-center">
                  <span className="inline-block w-3 h-3 bg-green-500 rounded-full mr-2"></span>
                  Strengths
                </h3>
                <ul className="text-sm space-y-1 pl-5">
                  {strengths.length > 0 ? (
                    strengths.map((item, i) => (
                      <li key={i} className="list-disc text-gray-700">{item}</li>
                    ))
                  ) : (
                    <li className="text-gray-500 italic">No strengths identified yet</li>
                  )}
                </ul>
              </div>
              
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-red-700 flex items-center">
                  <span className="inline-block w-3 h-3 bg-red-500 rounded-full mr-2"></span>
                  Weaknesses
                </h3>
                <ul className="text-sm space-y-1 pl-5">
                  {weaknesses.length > 0 ? (
                    weaknesses.map((item, i) => (
                      <li key={i} className="list-disc text-gray-700">{item}</li>
                    ))
                  ) : (
                    <li className="text-gray-500 italic">No weaknesses identified yet</li>
                  )}
                </ul>
              </div>
              
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-blue-700 flex items-center">
                  <span className="inline-block w-3 h-3 bg-blue-500 rounded-full mr-2"></span>
                  Opportunities
                </h3>
                <ul className="text-sm space-y-1 pl-5">
                  {opportunities.length > 0 ? (
                    opportunities.map((item, i) => (
                      <li key={i} className="list-disc text-gray-700">{item}</li>
                    ))
                  ) : (
                    <li className="text-gray-500 italic">No opportunities identified yet</li>
                  )}
                </ul>
              </div>
              
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-amber-700 flex items-center">
                  <span className="inline-block w-3 h-3 bg-amber-500 rounded-full mr-2"></span>
                  Challenges
                </h3>
                <ul className="text-sm space-y-1 pl-5">
                  {threats.length > 0 ? (
                    threats.map((item, i) => (
                      <li key={i} className="list-disc text-gray-700">{item}</li>
                    ))
                  ) : (
                    <li className="text-gray-500 italic">No challenges identified yet</li>
                  )}
                </ul>
              </div>
            </div>
          )}

          {!isLoading && (strengths.length === 0 && weaknesses.length === 0) && (
            <div className="flex items-center justify-center py-4">
              <div className="flex items-center text-amber-600 bg-amber-50 px-4 py-2 rounded-lg">
                <AlertTriangle className="h-4 w-4 mr-2" />
                <p className="text-sm">Complete more quizzes to generate a detailed learning analysis</p>
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default SWOTAnalysis;
